// @flow
// external libs
import { createReducer, createActions } from 'reduxsauce'
import Immutable from 'seamless-immutable'

// config
import { config } from '../../Config/ConfigLoader'

// filter
import { actionFilterData, functionalFilterDataCheck } from '../FilterFunctions'

// sort
import { sortObjectByProps } from '../SortFunctions'

// types
import type { FilterStateType, FilterFilteredDataType, FiltersDataType } from './Types/FilterStateType'
import type { InsuranceCompanyType } from '../Types/InsuranceCompanyTypes'

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  setFilterData: ['data'],
  resetFilterFunctionalData: ['filterKey'],
  startFetchingForAll: ['filterKey', 'enabledCalculators'],
  onChangeFilterData: ['data', 'dataToFilter', 'filterKey'],
  onChangeFilterValue: ['blockName', 'name', 'checked', 'dataToFilter', 'filterKey'],
  onChangeFilterValueForAll: ['blockName', 'name', 'checked', 'dataToFilterWithKeys'],
  onAddToFilteredData: ['dataToFilter', 'filterKey', 'alwaysTurnOffFetchingAndEnable', 'turnOffFetching'],
  onRefreshFilteredData: ['oldData', 'newData', 'filterKey'],
  onRemoveFromFilteredData: ['toRemove', 'filterKey'],
  onToggleFilterTextMode: ['filterInTextMode'],
  onFilterInit: ['dataToFilter', 'filterKey', 'alwaysTurnOffFetching'],
  initInsuranceCompanies: ['insuranceCompanies', 'calculatorStartWith'],
  disableNotUsedInFunctionalData: ['expandedQuotes', 'filterKey', 'alwaysEnabled'],
  setEnabledCalculators: ['enabledCalculators'],
  refreshEnabledCalculators: null,
  resetFilterData: null,
})

export const FilterTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const initDataForFilter = (keys, defaultValue) =>
  keys.reduce((acc, key) => {
    acc[key] = defaultValue
    return acc
  }, {})

export const INITIAL_STATE: FilterStateType = Immutable({
  filterKeyPath: config.getConfig('quoteListContainer.filterConfig.filterKeyPath'),
  filterInTextMode: false,
  blocks: config.getConfig('quoteListContainer.filterConfig.blocks'),
  data: initDataForFilter(config.getConfig('quoteListContainer.filterBlockKeys'), {}),
  filteredData: initDataForFilter(config.getConfig('quoteListContainer.filterBlockKeys'), []),
  functionalData: initDataForFilter(config.getConfig('quoteListContainer.filterBlockKeys'), { insuranceCompany: {} }),
  enabledCalculators: [],
})

/* ------------- Reducers ------------- */

export const resetFilterFunctionalDataR = (
  state: FilterStateType,
  { filterKey }: { filterKey: string },
): FilterStateType => state.setIn(['functionalData', filterKey], INITIAL_STATE.functionalData[filterKey])

export const resetFilterDataR = (state: FilterStateType) =>
  state.set('filteredData', INITIAL_STATE.filteredData).set('data', INITIAL_STATE.data)

export const startFetchingForAllR = (
  state: FilterStateType,
  { filterKey, enabledCalculators }: { filterKey: string, enabledCalculators: Array<string> },
): FilterStateType =>
  state.setIn(
    ['functionalData', filterKey, 'insuranceCompany'],
    Object.keys(state.functionalData[filterKey].insuranceCompany).reduce((acc, cName: string) => {
      let fetching =
        state.functionalData[filterKey].insuranceCompany[cName].calculators &&
        state.functionalData[filterKey].insuranceCompany[cName].calculators.length > 0
      if (enabledCalculators.length > 0 && state.functionalData[filterKey].insuranceCompany[cName].calculators) {
        fetching = state.functionalData[filterKey].insuranceCompany[cName].calculators.reduce(
          (fetchingAcc, calcName) => fetchingAcc || enabledCalculators.includes(calcName),
          false,
        )
      }
      acc[cName] = state.functionalData[filterKey].insuranceCompany[cName]
        .set('fetching', fetching)
        .set('enabled', fetching)
      return acc
    }, {}),
  )

export const onChangeFilterValueR = (
  state: FilterStateType,
  {
    blockName,
    name,
    checked,
    dataToFilter,
    filterKey,
  }: { blockName: string, name: string, checked: boolean, dataToFilter: Array<any>, filterKey: string },
): FilterStateType => {
  const newState = state.setIn(['data', filterKey, blockName, name], checked)
  return newState.setIn(
    ['filteredData', filterKey],
    actionFilterData(state.filterKeyPath[filterKey], state.blocks[filterKey], newState.data[filterKey], dataToFilter),
  )
}

const onChangeFilterDataR = (
  state: FilterStateType,
  { data, dataToFilter, filterKey }: { data: Object, dataToFilter: Array<any>, filterKey: string },
) => {
  const newState = state.setIn(['data', filterKey], state.getIn(['data', filterKey]).merge(data, { deep: true }))
  return newState.setIn(
    ['filteredData', filterKey],
    actionFilterData(state.filterKeyPath[filterKey], state.blocks[filterKey], newState.data[filterKey], dataToFilter),
  )
}

const onChangeFilterValueForAllR = (
  state: FilterStateType,
  {
    blockName,
    name,
    checked,
    dataToFilterWithKeys,
  }: { blockName: string, name: string, checked: boolean, dataToFilterWithKeys: { [key: string]: Array<any> } },
): FilterStateType =>
  Object.keys(state.data).reduce(
    (acc, filterKey) =>
      acc
        .setIn(['data', filterKey, blockName, name], checked)
        .setIn(
          ['filteredData', filterKey],
          actionFilterData(
            acc.filterKeyPath[filterKey],
            acc.blocks[filterKey],
            acc.data[filterKey],
            dataToFilterWithKeys[filterKey],
          ),
        ),
    state,
  )

export const onAddToFilteredDataR = (
  state: FilterStateType,
  {
    dataToFilter,
    filterKey,
    alwaysTurnOffFetchingAndEnable,
    turnOffFetching,
  }: {
    dataToFilter: Array<any>,
    filterKey: string,
    alwaysTurnOffFetchingAndEnable: Array<string>,
    turnOffFetching: Array<string>,
  },
): FilterStateType => {
  const filteredData = state.filteredData[filterKey].asMutable()
  const data = Array.isArray(dataToFilter) ? dataToFilter : [dataToFilter]
  const add = actionFilterData(state.filterKeyPath[filterKey], state.blocks[filterKey], state.data[filterKey], data)
  return state
    .setIn(['filteredData', filterKey], [...filteredData, ...add])
    .setIn(
      ['functionalData', filterKey],
      functionalFilterDataCheck(
        state.blocks[filterKey],
        state.functionalData[filterKey],
        data,
        alwaysTurnOffFetchingAndEnable,
        turnOffFetching,
      ),
    )
}

export const onRefreshFilteredDataR = (
  state: FilterStateType,
  {
    oldData,
    newData,
    filterKey,
  }: { oldData: FilterFilteredDataType, newData: FilterFilteredDataType, filterKey: string },
): FilterStateType =>
  state.setIn(
    ['filteredData', filterKey],
    state.filteredData[filterKey].map((data) => (data === oldData ? newData : data)),
  )

export const onRemoveFromFilteredDataR = (
  state: FilterStateType,
  { toRemove, filterKey }: { toRemove: string, filterKey: string },
): FilterStateType =>
  state.setIn(['filteredData', filterKey], state.filteredData[filterKey].filter((data) => data !== toRemove))

export const onToggleFilterTextModeR = (state: FilterStateType): FilterStateType =>
  state.merge({ filterInTextMode: !state.filterInTextMode })

export const onFilterInitR = (
  state: FilterStateType,
  {
    dataToFilter,
    filterKey,
    alwaysTurnOffFetching,
  }: { dataToFilter: Array<any>, filterKey: string, alwaysTurnOffFetching?: Array<string> },
): FilterStateType =>
  state
    .setIn(
      ['filteredData', filterKey],
      actionFilterData(state.filterKeyPath[filterKey], state.blocks[filterKey], state.data[filterKey], dataToFilter),
    )
    .setIn(
      ['functionalData', filterKey],
      functionalFilterDataCheck(
        state.blocks[filterKey],
        state.functionalData[filterKey],
        dataToFilter,
        alwaysTurnOffFetching,
      ),
    )

const disableNotUsedInFunctionalDataR = (state: FilterStateType, { expandedQuotes, filterKey, alwaysEnabled }) => {
  const alwaysEnabledArray = Array.isArray(alwaysEnabled) ? alwaysEnabled : []
  const insuranceCompanyKeys = Object.keys(
    expandedQuotes.reduce(
      (acc, quote) => {
        if (quote.insuranceCompany) {
          acc[quote.insuranceCompany] = true
        }
        return acc
      },
      {
        ...alwaysEnabledArray.reduce((enabledAcc, insuranceCompany) => {
          enabledAcc[insuranceCompany] = true
          return enabledAcc
        }, {}),
      },
    ),
  )
  return state.setIn(
    ['functionalData', filterKey, 'insuranceCompany'],
    state.functionalData[filterKey]
      ? Object.keys(state.functionalData[filterKey].insuranceCompany).reduce(
          (acc, insuranceCompanyKey) =>
            acc
              .setIn([insuranceCompanyKey, 'enabled'], insuranceCompanyKeys.includes(insuranceCompanyKey))
              .setIn([insuranceCompanyKey, 'fetching'], !insuranceCompanyKeys.includes(insuranceCompanyKey)),
          state.functionalData[filterKey].insuranceCompany,
        )
      : {},
  )
}

export const initInsuranceCompaniesR = (
  state: FilterStateType,
  {
    insuranceCompanies,
    calculatorStartWith,
  }: { insuranceCompanies: { [key: string]: InsuranceCompanyType }, calculatorStartWith: string },
): FilterStateType => {
  let functionalData = state.functionalData
  const insuranceCompaniesSorted = sortObjectByProps(insuranceCompanies, 'shortName')
  const blocks = Object.keys(state.blocks).reduce((acc, blockKey) => {
    let filterBlocks = state.blocks[blockKey]
    acc[blockKey] = []
    filterBlocks.map((block) => {
      if (block.name === 'insuranceCompany') {
        block = block.asMutable()
        const calculators = {}
        block.options = Object.keys(insuranceCompaniesSorted)
          .filter(
            (key) =>
              insuranceCompaniesSorted[key].calculators &&
              insuranceCompaniesSorted[key].calculators.filter((calculatorName) =>
                calculatorName.startsWith(calculatorStartWith),
              ).length,
          )
          .map((key) => {
            const insuranceCompany = { ...insuranceCompaniesSorted[key] }
            calculators[key] = {
              fetching: false,
              webLogoGrey: insuranceCompany.webLogoGrey,
              enabled: false,
              calculators: insuranceCompaniesSorted[key].calculators,
            }
            insuranceCompany.value = key
            insuranceCompany.image = insuranceCompany.webLogo
            insuranceCompany.key = key
            return insuranceCompany
          })
        functionalData = functionalData
          .setIn([blockKey, 'insuranceCompany'], calculators)
          .setIn([blockKey, 'initialized'], true)
      }
      acc[blockKey].push(block)
    })
    return acc
  }, {})
  return state.merge({ blocks, functionalData })
}

const setEnabledCalculatorsR = (
  state: FilterStateType,
  { enabledCalculators }: { enabledCalculators: Array<string> },
) => state.set('enabledCalculators', enabledCalculators)

const setFilterDataR = (state: FilterStateType, { data }: { data: FiltersDataType }) => state.set('data', data)

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.SET_FILTER_DATA]: setFilterDataR,
  [Types.ON_CHANGE_FILTER_VALUE]: onChangeFilterValueR,
  [Types.ON_CHANGE_FILTER_VALUE_FOR_ALL]: onChangeFilterValueForAllR,
  [Types.ON_CHANGE_FILTER_DATA]: onChangeFilterDataR,
  [Types.ON_ADD_TO_FILTERED_DATA]: onAddToFilteredDataR,
  [Types.ON_REFRESH_FILTERED_DATA]: onRefreshFilteredDataR,
  [Types.ON_REMOVE_FROM_FILTERED_DATA]: onRemoveFromFilteredDataR,
  [Types.ON_TOGGLE_FILTER_TEXT_MODE]: onToggleFilterTextModeR,
  [Types.ON_FILTER_INIT]: onFilterInitR,
  [Types.RESET_FILTER_FUNCTIONAL_DATA]: resetFilterFunctionalDataR,
  [Types.INIT_INSURANCE_COMPANIES]: initInsuranceCompaniesR,
  [Types.START_FETCHING_FOR_ALL]: startFetchingForAllR,
  [Types.DISABLE_NOT_USED_IN_FUNCTIONAL_DATA]: disableNotUsedInFunctionalDataR,
  [Types.SET_ENABLED_CALCULATORS]: setEnabledCalculatorsR,
  [Types.REFRESH_ENABLED_CALCULATORS]: (state) => state,
  [Types.RESET_FILTER_DATA]: resetFilterDataR,
})
